import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';

const PhoneInput = ({ 
  value = '', 
  onChange, 
  error, 
  required = false,
  className = '' 
}) => {
  const { t } = useTranslation();
  // Split value into country code and phone number
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Parse existing value on mount
  React.useEffect(() => {
    if (value) {
      // Try to extract country code and number from value
      const match = value.match(/^(\+\d{1,3})\s?(.+)$/);
      if (match) {
        setCountryCode(match[1]);
        setPhoneNumber(match[2].replace(/\D/g, ''));
      } else {
        setPhoneNumber(value.replace(/\D/g, ''));
      }
    }
  }, []);

  // Format phone number with spaces (e.g., "555 123 4567")
  const formatPhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    
    if (countryCode === '+1') {
      // US/Canada format: (XXX) XXX-XXXX
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    } else {
      // International format: XXX XXX XXXX
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
      if (cleaned.length <= 9) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, ''); // Remove non-digits
    const maxLength = countryCode === '+1' ? 10 : 15;
    
    if (input.length <= maxLength) {
      setPhoneNumber(input);
      const fullNumber = `${countryCode} ${formatPhoneNumber(input)}`.trim();
      onChange(fullNumber);
    }
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);
    const fullNumber = `${newCode} ${formatPhoneNumber(phoneNumber)}`.trim();
    onChange(fullNumber);
  };

  const displayValue = formatPhoneNumber(phoneNumber);

  return (
    <div className={className}>
      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
        {t('booking.phone')} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Phone className="h-5 w-5 text-gray-400" />
        </div>
        <div className="flex gap-2">
          {/* Country Code Dropdown */}
          <select
            value={countryCode}
            onChange={handleCountryCodeChange}
            className="pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-40"
          >
            <option value="+1">🇨🇦🇺🇸 +1</option>
            <option value="+44">🇬🇧 +44</option>
            <option value="+33">🇫🇷 +33</option>
            <option value="+49">🇩🇪 +49</option>
            <option value="+86">🇨🇳 +86</option>
            <option value="+81">🇯🇵 +81</option>
            <option value="+82">🇰🇷 +82</option>
            <option value="+91">🇮🇳 +91</option>
            <option value="+61">🇦🇺 +61</option>
            <option value="+55">🇧🇷 +55</option>
            <option value="+52">🇲🇽 +52</option>
            <option value="+34">🇪🇸 +34</option>
            <option value="+39">🇮🇹 +39</option>
            <option value="+7">🇷🇺 +7</option>
          </select>
          
          {/* Phone Number Input */}
          <input
            type="tel"
            id="phone"
            value={displayValue}
            onChange={handlePhoneChange}
            placeholder="555 123 4567"
            className={`flex-1 pl-3 pr-3 py-3 border ${
              error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            } rounded-lg shadow-sm focus:ring-2 transition-all`}
          />
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
          <span>⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
